import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Database, Search, TrendingUp, Zap, BarChart3, Home, Activity } from 'lucide-react';

interface HeaderProps {
  activeTab: string;
  onTabChange: (tab: string) => void;
}

const Header: React.FC<HeaderProps> = ({ activeTab, onTabChange }) => {
  const location = useLocation();

  const navigation = [
    { id: 'dashboard', name: 'Dashboard', path: '/', icon: Home },
    { id: 'documents', name: 'Documents', path: '/documents', icon: Database },
    { id: 'vectors', name: 'Vector Search', path: '/vectors', icon: Search },
    { id: 'timeseries', name: 'Time Series', path: '/timeseries', icon: TrendingUp },
    { id: 'realtime', name: 'Real-Time', path: '/realtime', icon: Zap },
    { id: 'analytics', name: 'Analytics', path: '/analytics', icon: BarChart3 },
  ];

  return (
    <header className='bg-white/95 backdrop-blur-md border-b border-gray-200 sticky top-0 z-50'>
      <div className='container mx-auto px-4 sm:px-6 lg:px-8'>
        <div className='flex justify-between items-center h-16'>
          {/* Logo */}
          <Link to='/' className='flex items-center space-x-3'>
            <div className='bg-gradient-to-r from-blue-600 to-purple-600 p-2 rounded-lg'>
              <Database className='w-6 h-6 text-white' />
            </div>
            <div>
              <h1 className='text-xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent'>
                EdgeVector
              </h1>
              <p className='text-xs text-gray-500 -mt-1'>Edge-Native Database</p>
            </div>
          </Link>

          {/* Navigation */}
          <nav className='hidden md:flex space-x-1'>
            {navigation.map(item => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;

              return (
                <Link
                  key={item.id}
                  to={item.path}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 flex items-center space-x-2 ${
                    isActive
                      ? 'bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-lg'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                  }`}
                  onClick={() => onTabChange(item.id)}
                >
                  <Icon className='w-4 h-4' />
                  <span>{item.name}</span>
                </Link>
              );
            })}
          </nav>

          {/* Status Badge */}
          <div className='flex items-center space-x-4'>
            <div className='hidden sm:flex items-center space-x-2 bg-green-50 px-3 py-1 rounded-full'>
              <Activity className='w-4 h-4 text-green-600' />
              <span className='text-xs text-green-700 font-medium'>Live Database</span>
            </div>

            {/* Mobile Menu Button */}
            <button
              className='md:hidden p-2 rounded-lg hover:bg-gray-100'
              onClick={() => {
                // Mobile menu toggle would go here
                console.log('Mobile menu clicked');
              }}
            >
              <svg className='w-5 h-5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M4 6h16M4 12h16M4 18h16'
                />
              </svg>
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        <div className='md:hidden pb-4'>
          <div className='flex flex-wrap gap-2'>
            {navigation.map(item => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;

              return (
                <Link
                  key={item.id}
                  to={item.path}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 flex items-center space-x-1 ${
                    isActive
                      ? 'bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-lg'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                  }`}
                  onClick={() => onTabChange(item.id)}
                >
                  <Icon className='w-4 h-4' />
                  <span>{item.name}</span>
                </Link>
              );
            })}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
